// @spec SCF-005
// @spec SHL-STR-001
// @spec SHL-STR-002
// @spec SHL-STR-006
// @spec SHL-STR-016
// @spec SHL-STR-044
// App-level Zustand store with auto-selectors pattern and localStorage persistence

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import { createSelectors } from './create-selectors';

interface AppState {
  // Sidebar collapsed state
  sidebarCollapsed: boolean;
  setSidebarCollapsed: (collapsed: boolean) => void;
  toggleSidebar: () => void;
  // Track if user has explicitly set sidebar state (for tablet default behavior)
  sidebarUserPreferenceSet: boolean;
  setSidebarUserPreferenceSet: (set: boolean) => void;

  // Mobile navigation state (separate from desktop sidebar)
  mobileNavOpen: boolean;
  setMobileNavOpen: (open: boolean) => void;
  toggleMobileNav: () => void;

  // Navigation group expansion state
  expandedNavGroups: string[];
  toggleNavGroup: (groupId: string) => void;
  setExpandedNavGroups: (groups: string[]) => void;

  // Global search dialog state (not persisted - always closed on page load)
  searchOpen: boolean;
  setSearchOpen: (open: boolean) => void;
  toggleSearch: () => void;

  // AI features toggle (persisted)
  aiEnabled: boolean;
  setAIEnabled: (enabled: boolean) => void;
}

const useAppStoreBase = create<AppState>()(
  persist(
    (set) => ({
      sidebarCollapsed: false,
      sidebarUserPreferenceSet: false,

      setSidebarCollapsed: (collapsed: boolean) => {
        set({ sidebarCollapsed: collapsed, sidebarUserPreferenceSet: true });
      },

      toggleSidebar: () => {
        set((state) => ({
          sidebarCollapsed: !state.sidebarCollapsed,
          sidebarUserPreferenceSet: true,
        }));
      },

      setSidebarUserPreferenceSet: (preferenceSet: boolean) => {
        set({ sidebarUserPreferenceSet: preferenceSet });
      },

      // Mobile navigation state (not persisted - always closed on page load)
      mobileNavOpen: false,

      setMobileNavOpen: (open: boolean) => {
        set({ mobileNavOpen: open });
      },

      toggleMobileNav: () => {
        set((state) => ({ mobileNavOpen: !state.mobileNavOpen }));
      },

      // Navigation group expansion state - default to visible top-level groups
      expandedNavGroups: ['home', 'task-management', 'frameworks'],

      toggleNavGroup: (groupId: string) => {
        set((state) => {
          const isExpanded = state.expandedNavGroups.includes(groupId);
          if (isExpanded) {
            return {
              expandedNavGroups: state.expandedNavGroups.filter((id) => id !== groupId),
            };
          }
          return {
            expandedNavGroups: [...state.expandedNavGroups, groupId],
          };
        });
      },

      setExpandedNavGroups: (groups: string[]) => {
        set({ expandedNavGroups: groups });
      },

      // Global search state (not persisted)
      searchOpen: false,

      setSearchOpen: (open: boolean) => {
        set({ searchOpen: open });
      },

      toggleSearch: () => {
        set((state) => ({ searchOpen: !state.searchOpen }));
      },

      // AI features toggle
      aiEnabled: true,

      setAIEnabled: (enabled: boolean) => {
        set({ aiEnabled: enabled });
      },
    }),
    {
      name: 'quantifier-app-store',
      partialize: (state) => ({
        sidebarCollapsed: state.sidebarCollapsed,
        sidebarUserPreferenceSet: state.sidebarUserPreferenceSet,
        expandedNavGroups: state.expandedNavGroups,
        aiEnabled: state.aiEnabled,
      }),
    }
  )
);

/**
 * App-level store for UI state that doesn't belong in server state.
 *
 * Uses auto-selectors pattern for type-safe property access.
 *
 * @example
 * // Using auto-selectors (recommended)
 * const sidebarCollapsed = useAppStore.use.sidebarCollapsed();
 * const toggleSidebar = useAppStore.use.toggleSidebar();
 *
 * @example
 * // Using traditional selector
 * const sidebarCollapsed = useAppStore((state) => state.sidebarCollapsed);
 */
export const useAppStore = createSelectors(useAppStoreBase);
